<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <header class="card-header">
            <strong>Workshop Participativo do PMAU</strong>
          </header>

          <!-- Loading inicial -->
          <div v-if="loading" class="loading-container">
            <RotateSquare class="loading-position animated fadeIn" size="60px" />
          </div>

          <!-- Sem evento: tela de criação -->
          <div v-else-if="!evento" class="card-body text-center py-5">
            <i class="fa fa-calendar-plus-o fa-3x text-muted mb-3 d-block"></i>
            <h5 class="text-muted mb-2">Nenhum workshop cadastrado</h5>
            <p class="text-muted mb-4">Crie o primeiro workshop participativo do PMAU para começar a receber inscrições.</p>
            <button class="btn btn-success" @click="abrirModalCriar">
              <i class="fa fa-plus mr-1"></i> Criar Workshop
            </button>
          </div>

          <!-- Com evento: painel em abas -->
          <div v-else class="card-body p-0">
            <b-tabs card @activate-tab="onTabAtivado">

              <!-- ── Aba 1: Configuração ─────────────────────────────────── -->
              <b-tab title="Configuração" active>
                <div class="row">

                  <!-- Lado esquerdo: campos do evento -->
                  <div class="col-lg-7">
                    <div class="form-group">
                      <label><strong>Título do Workshop</strong></label>
                      <b-form-input v-model="evento.title" placeholder="Ex: Workshop Participativo do PMAU 2025" />
                    </div>
                    <div class="form-group">
                      <label><strong>Descrição</strong></label>
                      <b-form-textarea v-model="evento.description" rows="3" placeholder="Detalhes sobre o workshop..." />
                    </div>
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label><strong>Data e Hora</strong></label>
                          <b-form-input v-model="evento.event_date_local" type="datetime-local" />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label><strong>Local</strong></label>
                          <b-form-input v-model="evento.location" placeholder="Endereço ou nome do local" />
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label><strong>Prazo de Inscrição</strong> <span class="text-muted small">(opcional)</span></label>
                          <b-form-input v-model="evento.registration_deadline_local" type="datetime-local" />
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="form-group">
                          <label><strong>Capacidade</strong> <span class="text-muted small">(opcional)</span></label>
                          <b-form-input v-model.number="evento.max_capacity" type="number" min="1" placeholder="Sem limite" />
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="form-group">
                          <label><strong>Status</strong></label>
                          <b-form-select v-model="evento.status" :options="statusOpcoes" />
                        </div>
                      </div>
                    </div>
                    <button class="btn btn-primary" :disabled="salvando" @click="salvarEvento">
                      {{ salvando ? "Salvando..." : "Salvar alterações" }}
                    </button>
                    <button class="btn btn-outline-success ml-2" @click="abrirModalCriar">
                      <i class="fa fa-plus mr-1"></i> Novo Workshop
                    </button>
                  </div>

                  <!-- Lado direito: link público e stats -->
                  <div class="col-lg-5">
                    <div class="card bg-light border">
                      <div class="card-body">
                        <h6 class="mb-3"><i class="fa fa-link mr-1"></i> Links públicos</h6>
                        <div class="mb-2">
                          Status atual:
                          <b-badge :variant="statusVariant(evento.status)" class="ml-1">
                            {{ statusLabel(evento.status) }}
                          </b-badge>
                        </div>
                        <label class="small font-weight-bold mb-1">Inscrição</label>
                        <div class="input-group mb-2">
                          <b-form-input :value="publicUrl" readonly size="sm" />
                          <div class="input-group-append">
                            <button class="btn btn-outline-secondary btn-sm" @click="copiarLink('inscricao')" title="Copiar link de inscrição">
                              <i class="fa fa-copy"></i>
                            </button>
                          </div>
                        </div>
                        <label class="small font-weight-bold mb-1">
                          Confirmação de presença
                          <span class="text-muted font-weight-normal">(disponível no dia do evento)</span>
                        </label>
                        <div class="input-group mb-2">
                          <b-form-input :value="confirmacaoUrl" readonly size="sm" />
                          <div class="input-group-append">
                            <button class="btn btn-outline-secondary btn-sm" @click="copiarLink('confirmacao')" title="Copiar link de confirmação">
                              <i class="fa fa-copy"></i>
                            </button>
                          </div>
                        </div>
                        <small class="text-muted">
                          Inscrições abertas somente quando status for <strong>Aberto</strong>.
                        </small>
                      </div>
                    </div>

                    <div class="card border mt-3">
                      <div class="card-body text-center">
                        <div class="row">
                          <div class="col-4 border-right">
                            <h3 class="mb-0 text-primary">{{ evento.total_registrations || 0 }}</h3>
                            <small class="text-muted">inscritos</small>
                          </div>
                          <div class="col-4 border-right">
                            <h3 class="mb-0 text-success">{{ evento.confirmed || 0 }}</h3>
                            <small class="text-muted">confirmados</small>
                          </div>
                          <div class="col-4">
                            <h3 class="mb-0 text-warning">{{ evento.pending || 0 }}</h3>
                            <small class="text-muted">pendentes</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-tab>

              <!-- ── Aba 2: Programação ──────────────────────────────────── -->
              <b-tab title="Programação">

                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="text-muted small">
                    <template v-if="programacao.length">{{ programacao.length }} item(s) na programação</template>
                    <template v-else>Nenhum item cadastrado.</template>
                  </span>
                  <button v-if="!adicionandoItem" class="btn btn-primary btn-sm" @click="adicionandoItem = true">
                    <i class="fa fa-plus mr-1"></i> Adicionar item
                  </button>
                </div>

                <!-- Formulário novo item -->
                <div v-if="adicionandoItem" class="card bg-light border mb-3">
                  <div class="card-body py-3">
                    <div class="row">
                      <div class="col-sm-2">
                        <div class="form-group mb-2">
                          <label class="small font-weight-bold mb-1">Início <span class="text-danger">*</span></label>
                          <input type="time" class="form-control form-control-sm" v-model="novoItemProg.start_time" />
                        </div>
                      </div>
                      <div class="col-sm-2">
                        <div class="form-group mb-2">
                          <label class="small font-weight-bold mb-1">Fim <span class="text-danger">*</span></label>
                          <input type="time" class="form-control form-control-sm" v-model="novoItemProg.end_time" />
                        </div>
                      </div>
                      <div class="col-sm-4">
                        <div class="form-group mb-2">
                          <label class="small font-weight-bold mb-1">Atividade <span class="text-danger">*</span></label>
                          <input type="text" class="form-control form-control-sm" v-model="novoItemProg.activity" placeholder="Nome da atividade" />
                        </div>
                      </div>
                      <div class="col-sm-4">
                        <div class="form-group mb-2">
                          <label class="small font-weight-bold mb-1">Descrição <span class="text-muted font-weight-normal">(opcional)</span></label>
                          <input type="text" class="form-control form-control-sm" v-model="novoItemProg.description" placeholder="Descrição..." />
                        </div>
                      </div>
                    </div>
                    <div class="d-flex">
                      <button class="btn btn-success btn-sm mr-2" @click="adicionarItemProg">Adicionar</button>
                      <button class="btn btn-outline-secondary btn-sm" @click="cancelarAdicionarItem">Cancelar</button>
                    </div>
                  </div>
                </div>

                <!-- Tabela de itens -->
                <table v-if="programacao.length" class="table table-bordered table-sm mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th style="width:90px">Início</th>
                      <th style="width:90px">Fim</th>
                      <th style="width:220px">Atividade</th>
                      <th>Descrição</th>
                      <th style="width:85px" class="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in programacao" :key="idx">
                      <template v-if="editandoProgIdx === idx">
                        <td><input type="time" class="form-control form-control-sm" v-model="item.start_time" /></td>
                        <td><input type="time" class="form-control form-control-sm" v-model="item.end_time" /></td>
                        <td><input type="text" class="form-control form-control-sm" v-model="item.activity" /></td>
                        <td><input type="text" class="form-control form-control-sm" v-model="item.description" /></td>
                        <td class="text-center text-nowrap">
                          <button class="btn btn-success btn-sm mr-1" @click="confirmarEditarProg" title="Confirmar">
                            <i class="fa fa-check"></i>
                          </button>
                          <button class="btn btn-secondary btn-sm" @click="cancelarEditarProg" title="Cancelar">
                            <i class="fa fa-times"></i>
                          </button>
                        </td>
                      </template>
                      <template v-else>
                        <td class="font-weight-bold text-primary align-middle">{{ item.start_time }}</td>
                        <td class="font-weight-bold text-primary align-middle">{{ item.end_time }}</td>
                        <td class="align-middle">{{ item.activity }}</td>
                        <td class="text-muted align-middle">{{ item.description }}</td>
                        <td class="text-center text-nowrap align-middle">
                          <button class="btn btn-secondary btn-sm mr-1" @click="iniciarEditarProg(idx)" title="Editar">
                            <i class="fa fa-edit"></i>
                          </button>
                          <button class="btn btn-outline-danger btn-sm" @click="removerItemProg(idx)" title="Remover">
                            <i class="fa fa-trash"></i>
                          </button>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </table>

                <div v-if="salvandoProgramacao" class="mt-2 text-muted small">
                  <i class="fa fa-spinner fa-spin mr-1"></i> Salvando...
                </div>

              </b-tab>

              <!-- ── Aba 3: Inscrições ───────────────────────────────────── -->
              <b-tab title="Inscrições">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center">
                    <span class="mr-3">
                      Total: <strong class="text-primary">{{ totalInscricoes }}</strong>
                    </span>
                    <b-form-checkbox v-model="filtroConfirmados" size="sm">
                      <small>Somente confirmados</small>
                    </b-form-checkbox>
                  </div>
                  <button
                    class="btn btn-success btn-sm d-inline-flex align-items-center"
                    :disabled="exportandoXlsx || totalInscricoes === 0"
                    @click="exportarXlsx"
                  >
                    <i class="fa fa-file-excel mr-1"></i>
                    {{ exportandoXlsx ? "Exportando..." : "Exportar Excel" }}
                  </button>
                </div>

                <div v-if="loadingInscricoes" class="text-center py-4">
                  <RotateSquare size="40px" />
                </div>

                <div v-else-if="!inscricoes.length" class="text-muted small py-2">
                  Nenhuma inscrição encontrada.
                </div>

                <div v-else>
                  <div v-for="r in inscricoes" :key="r.id" class="card mb-2">
                    <div class="card-body py-3 px-3">
                      <div class="d-flex justify-content-between align-items-start">
                        <div class="flex-grow-1 mr-3">
                          <p class="mb-1 font-weight-bold">
                            {{ r.full_name }}
                            <b-badge v-if="r.confirmed_at" variant="success" class="ml-1" style="font-size:0.65rem">confirmado</b-badge>
                            <b-badge v-else variant="secondary" class="ml-1" style="font-size:0.65rem">pendente</b-badge>
                          </p>
                          <div class="small text-muted">
                            <span v-if="r.email" class="mr-3"><i class="fa fa-envelope mr-1"></i>{{ r.email }}</span>
                            <span v-if="r.phone"><i class="fa fa-phone mr-1"></i>{{ r.phone }}</span>
                          </div>
                          <div class="small text-muted mt-1">
                            <span v-if="r.neighborhood_name" class="mr-3"><i class="fa fa-map-marker mr-1"></i>{{ r.neighborhood_name }}</span>
                            <span v-if="r.represents_organization === true">
                              <i class="fa fa-building mr-1"></i>{{ r.organization_name || "Empresa/Instituição" }}
                            </span>
                          </div>
                          <div class="small text-muted mt-1">
                            <span>Inscrito em: {{ formatarData(r.registered_at) }}</span>
                            <span v-if="r.confirmed_at" class="ml-3">Confirmado em: {{ formatarData(r.confirmed_at) }}</span>
                          </div>
                        </div>
                        <div class="flex-shrink-0">
                          <b-button
                            v-if="!r.confirmed_at"
                            variant="outline-success"
                            size="sm"
                            class="mr-1"
                            :disabled="confirmandoId === r.id"
                            @click="confirmarInscricao(r)"
                          >
                            <i class="fa fa-check"></i>
                          </b-button>
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            :disabled="removendoId === r.id"
                            @click="removerInscricao(r)"
                          >
                            <i class="fa fa-trash"></i>
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="totalInscricoes > limitInscricoes" class="d-flex justify-content-between align-items-center mt-3">
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      :disabled="skipInscricoes === 0"
                      @click="paginaAnterior"
                    >Anterior</button>
                    <small class="text-muted">
                      {{ skipInscricoes + 1 }}–{{ Math.min(skipInscricoes + limitInscricoes, totalInscricoes) }}
                      de {{ totalInscricoes }}
                    </small>
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      :disabled="skipInscricoes + limitInscricoes >= totalInscricoes"
                      @click="proximaPagina"
                    >Próxima</button>
                  </div>
                </div>
              </b-tab>

              <!-- ── Aba 4: Arte / QR ──────────────────────────────────── -->
              <b-tab title="Arte / QR">

                <!-- Linha 1: QR | Gerar Arte -->
                <div class="row">
                  <div class="col-lg-4">
                    <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-qrcode mr-1"></i> QR Code de Inscrição
                    </h6>
                    <div class="text-center">
                      <div v-if="loadingQr" class="py-4">
                        <RotateSquare size="30px" />
                      </div>
                      <img
                        v-else-if="qrBlobUrl"
                        :src="qrBlobUrl"
                        class="img-fluid border rounded"
                        style="max-width:200px"
                        alt="QR Code"
                      />
                      <div v-else class="text-muted small py-4">
                        QR Code não disponível.
                      </div>
                    </div>
                    <div class="text-center mt-2">
                      <button
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="!qrBlobUrl"
                        @click="baixarQr"
                      >
                        <i class="fa fa-download mr-1"></i> Baixar QR
                      </button>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted">
                        O QR Code aponta para a página pública de inscrição do workshop
                        e é embutido nas artes geradas pela IA.
                      </small>
                    </div>
                  </div>

                  <div class="col-lg-8">
                    <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-magic mr-1"></i> Arte de Divulgação com IA
                    </h6>
                    <div class="form-group">
                      <label>
                        <strong>Prompt</strong>
                        <span class="text-muted small ml-1">(editável antes de gerar)</span>
                      </label>
                      <b-form-textarea
                        v-model="artworkPrompt"
                        rows="4"
                        :disabled="gerandoArte || loadingDefaultPrompt"
                        placeholder="Carregando prompt automático..."
                      />
                    </div>
                    <button
                      class="btn btn-success"
                      :disabled="gerandoArte || !artworkPrompt.trim()"
                      @click="gerarArte"
                    >
                      <span v-if="gerandoArte">
                        <i class="fa fa-spinner fa-spin mr-1"></i> Gerando arte, aguarde... (~15–30s)
                      </span>
                      <span v-else>
                        <i class="fa fa-magic mr-1"></i> Gerar com IA
                      </span>
                    </button>
                  </div>
                </div>

                <!-- Linha 2: Última Arte Gerada | Histórico (alinhados) -->
                <div class="row mt-4">
                  <div class="col-lg-4">
                    <template v-if="artworks.length && artworks[0].file_url">
                      <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                        <i class="fa fa-image mr-1"></i> Última Arte Gerada
                      </h6>
                      <div class="border rounded overflow-hidden">
                        <img :src="artworks[0].file_url" class="img-fluid d-block w-100" alt="Última arte gerada" />
                      </div>
                      <button class="btn btn-outline-primary btn-sm mt-2 btn-block" @click="baixarArte(artworks[0])">
                        <i class="fa fa-download mr-1"></i> Baixar Arte
                      </button>
                    </template>
                  </div>

                  <div class="col-lg-8">
                    <template v-if="artworks.length > 0">
                      <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                        Histórico
                      </h6>
                      <div class="row no-gutters">
                        <div v-for="a in artworks" :key="a.id" class="col-4 col-md-3 mb-3 pr-2">
                          <div class="card border h-100">
                            <div
                              class="d-flex align-items-center justify-content-center bg-light"
                              style="height:120px;overflow:hidden"
                            >
                              <img
                                v-if="a.file_url"
                                :src="a.file_url"
                                style="object-fit:cover;width:100%;height:120px"
                                alt="Arte"
                              />
                              <i v-else class="fa fa-image fa-2x text-muted"></i>
                            </div>
                            <div class="card-body p-2">
                              <small class="text-muted d-block" style="font-size:0.65rem">{{ formatarData(a.generated_at) }}</small>
                              <small class="text-muted d-block" style="font-size:0.65rem">
                                {{ a.source === "upload" ? "Upload" : (a.quality_used || "") }}
                              </small>
                              <div class="mt-1 d-flex">
                                <button
                                  class="btn btn-outline-secondary btn-sm mr-1 flex-grow-1"
                                  :disabled="!a.file_url"
                                  @click="baixarArte(a)"
                                  title="Baixar"
                                >
                                  <i class="fa fa-download"></i>
                                </button>
                                <button
                                  class="btn btn-outline-danger btn-sm flex-grow-1"
                                  @click="excluirArte(a)"
                                  title="Excluir"
                                >
                                  <i class="fa fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Upload manual -->
                    <h6 class="text-uppercase text-muted mb-2" :class="artworks.length ? 'mt-2' : ''" style="font-size:0.72rem;letter-spacing:0.06em">
                      Upload Manual
                    </h6>
                    <div class="d-flex align-items-center">
                      <b-form-file
                        v-model="uploadArquivo"
                        accept="image/*"
                        placeholder="Selecione uma imagem..."
                        class="mr-2"
                        style="max-width:280px"
                      />
                      <button
                        class="btn btn-outline-secondary"
                        :disabled="!uploadArquivo || uploadandoArte"
                        @click="uploadArte"
                      >
                        <span v-if="uploadandoArte"><i class="fa fa-spinner fa-spin mr-1"></i></span>
                        <span v-else><i class="fa fa-upload mr-1"></i></span>
                        Enviar
                      </button>
                    </div>
                  </div>
                </div>

                <!-- ── Divisor ── -->
                <hr class="my-4" />

                <!-- ── Seção: Arte de Confirmação (Dia do Evento) ── -->
                <h5 class="mb-3">
                  <i class="fa fa-check-circle-o mr-1 text-success"></i>
                  Arte de Confirmação
                  <small class="text-muted" style="font-size:0.8rem;font-weight:400"> — exibida no dia do evento para confirmação de presença</small>
                </h5>

                <!-- Linha 1: QR | Gerar Arte -->
                <div class="row">
                  <div class="col-lg-4">
                    <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-qrcode mr-1"></i> QR Code de Confirmação
                    </h6>
                    <div class="text-center">
                      <div v-if="loadingConfirmQr" class="py-4">
                        <RotateSquare size="30px" />
                      </div>
                      <img
                        v-else-if="confirmQrBlobUrl"
                        :src="confirmQrBlobUrl"
                        class="img-fluid border rounded"
                        style="max-width:200px"
                        alt="QR Code Confirmação"
                      />
                      <div v-else class="text-muted small py-4">
                        QR Code não disponível.
                      </div>
                    </div>
                    <div class="text-center mt-2">
                      <button
                        class="btn btn-outline-secondary btn-sm"
                        :disabled="!confirmQrBlobUrl"
                        @click="baixarConfirmQr"
                      >
                        <i class="fa fa-download mr-1"></i> Baixar QR
                      </button>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted">
                        O QR Code aponta para a página de confirmação de presença, disponível apenas no dia do evento.
                      </small>
                    </div>
                  </div>

                  <div class="col-lg-8">
                    <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-magic mr-1"></i> Arte para Exibição no Dia com IA
                    </h6>
                    <div class="form-group">
                      <label>
                        <strong>Prompt</strong>
                        <span class="text-muted small ml-1">(editável antes de gerar)</span>
                      </label>
                      <b-form-textarea
                        v-model="confirmPrompt"
                        rows="4"
                        :disabled="gerandoConfirmArte || loadingConfirmPrompt"
                        placeholder="Carregando prompt automático..."
                      />
                    </div>
                    <button
                      class="btn btn-success"
                      :disabled="gerandoConfirmArte || !confirmPrompt.trim()"
                      @click="gerarConfirmArte"
                    >
                      <span v-if="gerandoConfirmArte">
                        <i class="fa fa-spinner fa-spin mr-1"></i> Gerando arte, aguarde... (~15–30s)
                      </span>
                      <span v-else>
                        <i class="fa fa-magic mr-1"></i> Gerar com IA
                      </span>
                    </button>
                  </div>
                </div>

                <!-- Linha 2: Última Arte Gerada | Histórico -->
                <div class="row mt-4">
                  <div class="col-lg-4">
                    <template v-if="confirmArtworks.length && confirmArtworks[0].file_url">
                      <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                        <i class="fa fa-image mr-1"></i> Última Arte Gerada
                      </h6>
                      <div class="border rounded overflow-hidden">
                        <img :src="confirmArtworks[0].file_url" class="img-fluid d-block w-100" alt="Última arte de confirmação" />
                      </div>
                      <button class="btn btn-outline-primary btn-sm mt-2 btn-block" @click="baixarArte(confirmArtworks[0])">
                        <i class="fa fa-download mr-1"></i> Baixar Arte
                      </button>
                    </template>
                  </div>

                  <div class="col-lg-8">
                    <template v-if="confirmArtworks.length > 0">
                      <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                        Histórico
                      </h6>
                      <div class="row no-gutters">
                        <div v-for="a in confirmArtworks" :key="a.id" class="col-4 col-md-3 mb-3 pr-2">
                          <div class="card border h-100">
                            <div
                              class="d-flex align-items-center justify-content-center bg-light"
                              style="height:120px;overflow:hidden"
                            >
                              <img
                                v-if="a.file_url"
                                :src="a.file_url"
                                style="object-fit:cover;width:100%;height:120px"
                                alt="Arte confirmação"
                              />
                              <i v-else class="fa fa-image fa-2x text-muted"></i>
                            </div>
                            <div class="card-body p-2">
                              <small class="text-muted d-block" style="font-size:0.65rem">{{ formatarData(a.generated_at) }}</small>
                              <small class="text-muted d-block" style="font-size:0.65rem">
                                {{ a.source === "upload" ? "Upload" : (a.quality_used || "") }}
                              </small>
                              <div class="mt-1 d-flex">
                                <button
                                  class="btn btn-outline-secondary btn-sm mr-1 flex-grow-1"
                                  :disabled="!a.file_url"
                                  @click="baixarArte(a)"
                                  title="Baixar"
                                >
                                  <i class="fa fa-download"></i>
                                </button>
                                <button
                                  class="btn btn-outline-danger btn-sm flex-grow-1"
                                  @click="excluirConfirmArte(a)"
                                  title="Excluir"
                                >
                                  <i class="fa fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Upload manual -->
                    <h6 class="text-uppercase text-muted mb-2" :class="confirmArtworks.length ? 'mt-2' : ''" style="font-size:0.72rem;letter-spacing:0.06em">
                      Upload Manual
                    </h6>
                    <div class="d-flex align-items-center">
                      <b-form-file
                        v-model="uploadConfirmArquivo"
                        accept="image/*"
                        placeholder="Selecione uma imagem..."
                        class="mr-2"
                        style="max-width:280px"
                      />
                      <button
                        class="btn btn-outline-secondary"
                        :disabled="!uploadConfirmArquivo || uploadandoConfirmArte"
                        @click="uploadConfirmArte"
                      >
                        <span v-if="uploadandoConfirmArte"><i class="fa fa-spinner fa-spin mr-1"></i></span>
                        <span v-else><i class="fa fa-upload mr-1"></i></span>
                        Enviar
                      </button>
                    </div>
                  </div>
                </div>

              </b-tab>

              <!-- ── Aba 4: Fotos ──────────────────────────────────────────── -->
              <b-tab title="Fotos">

                <!-- Upload -->
                <div class="row mb-4">
                  <div class="col-lg-8">
                    <h6 class="text-uppercase text-muted mb-3" style="font-size:0.72rem;letter-spacing:0.06em">
                      <i class="fa fa-upload mr-1"></i> Adicionar Foto
                    </h6>
                    <div class="form-group">
                      <b-form-file
                        v-model="fotoUploadArquivo"
                        accept="image/*"
                        placeholder="Selecione uma imagem..."
                      />
                    </div>
                    <div class="form-group">
                      <b-form-input
                        v-model="fotoUploadCaption"
                        placeholder="Legenda da foto (obrigatória — usada pela IA ao gerar o capítulo 10)"
                        maxlength="500"
                      />
                    </div>
                    <button
                      class="btn btn-primary"
                      :disabled="!fotoUploadArquivo || !fotoUploadCaption.trim() || uploadandoFoto"
                      @click="uploadFoto"
                    >
                      <span v-if="uploadandoFoto"><i class="fa fa-spinner fa-spin mr-1"></i> Enviando...</span>
                      <span v-else><i class="fa fa-upload mr-1"></i> Enviar foto</span>
                    </button>
                  </div>
                  <div class="col-lg-4">
                    <div class="alert alert-info py-2 small mt-4 mt-lg-0" style="margin-top:1.9rem!important">
                      <i class="fa fa-info-circle mr-1"></i>
                      As legendas e descrições geradas pela IA são usadas como contexto ao gerar o
                      <strong>Capítulo 10</strong> do PMAU (Diagnóstico Participativo — Workshop).
                    </div>
                  </div>
                </div>

                <!-- Grid de fotos -->
                <div v-if="loadingFotos" class="text-center py-4">
                  <RotateSquare size="40px" />
                </div>

                <div v-else-if="!fotos.length" class="text-muted small py-2">
                  Nenhuma foto cadastrada. Adicione fotos do workshop acima.
                </div>

                <div v-else class="row">
                  <div v-for="foto in fotos" :key="foto.id" class="col-sm-6 col-md-4 col-lg-3 mb-4">
                    <div class="card border h-100">
                      <!-- Thumbnail -->
                      <div
                        class="d-flex align-items-center justify-content-center bg-light"
                        style="height:160px;overflow:hidden;cursor:pointer"
                        @click="abrirFoto(foto)"
                      >
                        <img
                          v-if="foto.file_url"
                          :src="foto.file_url"
                          style="object-fit:cover;width:100%;height:160px"
                          :alt="foto.caption"
                        />
                        <i v-else class="fa fa-image fa-3x text-muted"></i>
                      </div>

                      <div class="card-body p-2">
                        <!-- Legenda editável -->
                        <div class="mb-2">
                          <b-form-input
                            v-model="foto._captionEdit"
                            size="sm"
                            placeholder="Legenda..."
                            @blur="salvarCaption(foto)"
                            @keyup.enter.native="salvarCaption(foto)"
                          />
                        </div>

                        <!-- Badge IA + descrição -->
                        <div v-if="foto.ai_description" class="mb-2">
                          <b-badge variant="success" class="mb-1" style="font-size:0.6rem">
                            <i class="fa fa-magic mr-1"></i>IA
                          </b-badge>
                          <p class="text-muted mb-0" style="font-size:0.7rem;line-height:1.3">
                            {{ foto.ai_description }}
                          </p>
                        </div>
                        <div v-else class="mb-2">
                          <b-badge variant="secondary" style="font-size:0.6rem">sem descrição IA</b-badge>
                        </div>

                        <!-- Ações -->
                        <div class="d-flex mt-1">
                          <button
                            class="btn btn-outline-success btn-sm flex-grow-1 mr-1"
                            :disabled="descrevendoId === foto.id"
                            @click="descreverFoto(foto)"
                            title="Gerar descrição com IA"
                          >
                            <span v-if="descrevendoId === foto.id">
                              <i class="fa fa-spinner fa-spin"></i>
                            </span>
                            <span v-else><i class="fa fa-magic"></i></span>
                          </button>
                          <button
                            class="btn btn-outline-danger btn-sm flex-grow-1"
                            @click="excluirFoto(foto)"
                            title="Excluir foto"
                          >
                            <i class="fa fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </b-tab>

              <!-- ── Aba 5: Presença no Dia ─────────────────────────────────── -->
              <b-tab title="Presença no Dia">
                <!-- Não é o dia do evento -->
                <div v-if="!isHojeEvento" class="alert alert-info py-3">
                  <i class="fa fa-calendar mr-2"></i>
                  A confirmação de presença estará disponível somente no dia do evento:
                  <strong>{{ formatarDataSimples(evento.event_date) }}</strong>
                </div>

                <!-- É o dia do evento -->
                <div v-else class="row">
                  <div class="col-lg-6">
                    <p class="text-muted small mb-3">
                      Digite o nome, e-mail ou telefone do participante para confirmar a presença.
                    </p>
                    <div class="input-group mb-2">
                      <b-form-input
                        v-model="identificadorCheckin"
                        placeholder="Nome, e-mail ou telefone"
                        @keyup.enter.native="fazerCheckin"
                      />
                      <div class="input-group-append">
                        <button
                          class="btn btn-primary"
                          :disabled="fazendoCheckin || !identificadorCheckin.trim()"
                          @click="fazerCheckin"
                        >
                          <span v-if="fazendoCheckin"><i class="fa fa-spinner fa-spin"></i></span>
                          <span v-else><i class="fa fa-check mr-1"></i>Confirmar</span>
                        </button>
                      </div>
                    </div>

                    <!-- Feedback do checkin -->
                    <div v-if="feedbackCheckin" class="alert py-2 small mt-2" :class="feedbackCheckin.type === 'success' ? 'alert-success' : feedbackCheckin.type === 'info' ? 'alert-info' : 'alert-danger'">
                      <i class="fa mr-1" :class="feedbackCheckin.type === 'success' ? 'fa-check-circle' : feedbackCheckin.type === 'info' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>
                      {{ feedbackCheckin.message }}
                    </div>
                  </div>

                  <div class="col-lg-6">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <h6 class="text-uppercase text-muted mb-0" style="font-size:0.72rem;letter-spacing:0.06em">
                        <i class="fa fa-list mr-1"></i> Confirmados
                      </h6>
                      <b-badge variant="success">{{ confirmadosPresenca.length }}</b-badge>
                    </div>
                    <div v-if="loadingConfirmados" class="text-center py-3">
                      <RotateSquare size="30px" />
                    </div>
                    <div v-else-if="!confirmadosPresenca.length" class="text-muted small py-2">
                      Nenhuma presença confirmada ainda.
                    </div>
                    <div
                      v-else
                      v-for="r in confirmadosPresenca"
                      :key="r.id"
                      class="d-flex justify-content-between align-items-center border-bottom py-2"
                    >
                      <div>
                        <strong class="d-block" style="font-size:0.9rem">{{ r.full_name }}</strong>
                        <small class="text-muted">{{ formatarHora(r.confirmed_at) }}</small>
                      </div>
                      <b-badge variant="success" style="font-size:0.65rem">
                        via {{ metodoLabel(r.confirmation_method) }}
                      </b-badge>
                    </div>
                  </div>
                </div>
              </b-tab>

            </b-tabs>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Modal: Criar Workshop ─────────────────────────────────────────── -->
    <b-modal
      v-model="modalCriar"
      title="Criar Workshop"
      ok-title="Criar"
      cancel-title="Cancelar"
      :ok-disabled="criando"
      @ok="criarEvento"
      @hidden="fecharModalCriar"
    >
      <div class="form-group">
        <label><strong>Título</strong> <span class="text-danger">*</span></label>
        <b-form-input v-model="novoEvento.title" placeholder="Ex: Workshop Participativo PMAU 2025" />
      </div>
      <div class="form-group">
        <label><strong>Descrição</strong> <span class="text-muted small">(opcional)</span></label>
        <b-form-textarea v-model="novoEvento.description" rows="2" />
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label><strong>Data e Hora</strong> <span class="text-danger">*</span></label>
            <b-form-input v-model="novoEvento.event_date" type="datetime-local" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label><strong>Local</strong> <span class="text-danger">*</span></label>
            <b-form-input v-model="novoEvento.location" placeholder="Endereço ou nome do local" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label><strong>Prazo de inscrição</strong> <span class="text-muted small">(opcional)</span></label>
            <b-form-input v-model="novoEvento.registration_deadline" type="datetime-local" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label><strong>Capacidade máxima</strong> <span class="text-muted small">(opcional)</span></label>
            <b-form-input v-model.number="novoEvento.max_capacity" type="number" min="1" placeholder="Sem limite" />
          </div>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import WorkshopService from "../../servico/WorkshopService";

const NOVO_EVENTO_VAZIO = () => ({
  title: "",
  description: "",
  event_date: "",
  location: "",
  registration_deadline: "",
  max_capacity: null
});

export default {
  name: "PmauWorkshop",
  components: { RotateSquare },
  data() {
    return {
      loading: false,
      evento: null,
      salvando: false,

      statusOpcoes: [
        { value: "draft", text: "Rascunho" },
        { value: "open", text: "Aberto" },
        { value: "closed", text: "Encerrado" },
        { value: "completed", text: "Realizado" }
      ],

      // Programação
      programacao: [],
      salvandoProgramacao: false,
      novoItemProg: { start_time: "", end_time: "", activity: "", description: "" },
      adicionandoItem: false,
      editandoProgIdx: null,
      editandoItemCopia: null,

      // Inscrições
      loadingInscricoes: false,
      inscricoes: [],
      totalInscricoes: 0,
      skipInscricoes: 0,
      limitInscricoes: 20,
      filtroConfirmados: false,
      confirmandoId: null,
      removendoId: null,
      exportandoXlsx: false,

      // Arte / QR — inscrição
      loadingQr: false,
      qrBlobUrl: null,
      artworkPrompt: "",
      loadingDefaultPrompt: false,
      artworkQuality: "high",
      gerandoArte: false,
      artworks: [],
      loadingArtworks: false,
      uploadArquivo: null,
      uploadandoArte: false,

      // Arte / QR — confirmação
      loadingConfirmQr: false,
      confirmQrBlobUrl: null,
      confirmPrompt: "",
      loadingConfirmPrompt: false,
      confirmQuality: "high",
      gerandoConfirmArte: false,
      confirmArtworks: [],
      loadingConfirmArtworks: false,
      uploadConfirmArquivo: null,
      uploadandoConfirmArte: false,

      // Fotos
      loadingFotos: false,
      fotos: [],
      fotoUploadArquivo: null,
      fotoUploadCaption: "",
      uploadandoFoto: false,
      descrevendoId: null,

      // Presença no dia
      identificadorCheckin: "",
      fazendoCheckin: false,
      feedbackCheckin: null,
      loadingConfirmados: false,
      confirmadosPresenca: [],

      // Modal criar
      modalCriar: false,
      criando: false,
      novoEvento: NOVO_EVENTO_VAZIO()
    };
  },
  computed: {
    contaId() {
      return this.$store.getters.getAutenticacao?.contaSelecionada?.id;
    },
    publicUrl() {
      if (!this.evento || !this.evento.public_token) return "";
      const base = window.location.origin + window.location.pathname;
      return base + "#/workshop/" + this.evento.public_token;
    },
    confirmacaoUrl() {
      if (!this.evento || !this.evento.public_token) return "";
      const base = window.location.origin + window.location.pathname;
      return base + "#/workshop/" + this.evento.public_token + "/confirmacao";
    },
    isHojeEvento() {
      if (!this.evento) return false;
      const evDate = new Date(this.evento.event_date);
      const today = new Date();
      return evDate.getFullYear() === today.getFullYear() &&
        evDate.getMonth() === today.getMonth() &&
        evDate.getDate() === today.getDate();
    }
  },
  watch: {
    filtroConfirmados() {
      this.skipInscricoes = 0;
      this.carregarInscricoes();
    }
  },
  beforeMount() {
    this.carregarEvento();
  },
  methods: {
    carregarEvento() {
      if (!this.contaId) return;
      this.loading = true;
      WorkshopService.ListEvents(this.contaId)
        .then((r) => {
          if (r.data && r.data.length) {
            const e = r.data[0];
            this.evento = {
              ...e,
              event_date_local: this.isoToLocal(e.event_date),
              registration_deadline_local: e.registration_deadline ? this.isoToLocal(e.registration_deadline) : ""
            };
            this.programacao = (e.schedule || []).slice().sort((a, b) => a.start_time.localeCompare(b.start_time));
          } else {
            this.evento = null;
          }
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar workshop."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.loading = false; });
    },

    salvarEvento() {
      if (!this.evento) return;
      this.salvando = true;
      const payload = {
        title: this.evento.title,
        description: this.evento.description,
        event_date: this.localToIso(this.evento.event_date_local),
        location: this.evento.location,
        registration_deadline: this.evento.registration_deadline_local
          ? this.localToIso(this.evento.registration_deadline_local)
          : null,
        max_capacity: this.evento.max_capacity || null,
        status: this.evento.status
      };
      WorkshopService.UpdateEvent(this.evento.id, payload)
        .then((r) => {
          this.evento = {
            ...r.data,
            event_date_local: this.isoToLocal(r.data.event_date),
            registration_deadline_local: r.data.registration_deadline ? this.isoToLocal(r.data.registration_deadline) : ""
          };
          this.$notify({ data: ["Workshop atualizado com sucesso."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao salvar workshop."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.salvando = false; });
    },

    // ── Programação ──────────────────────────────────────────────────────────

    _temSobreposicao(start, end, excludeIdx) {
      return this.programacao.some((item, idx) => {
        if (idx === excludeIdx) return false;
        return start < item.end_time && end > item.start_time;
      });
    },

    adicionarItemProg() {
      const { start_time, end_time, activity } = this.novoItemProg;
      if (!start_time || !end_time || !activity.trim()) {
        this.$notify({ data: ["Preencha hora início, hora fim e atividade."], type: "warn", duration: 3000 });
        return;
      }
      if (end_time <= start_time) {
        this.$notify({ data: ["A hora de fim deve ser posterior à hora de início."], type: "warn", duration: 3000 });
        return;
      }
      if (this._temSobreposicao(start_time, end_time, -1)) {
        this.$notify({ data: ["Este horário se sobrepõe a outro item da programação."], type: "warn", duration: 4000 });
        return;
      }
      this.programacao.push({
        start_time,
        end_time,
        activity: activity.trim(),
        description: this.novoItemProg.description.trim()
      });
      this.programacao.sort((a, b) => a.start_time.localeCompare(b.start_time));
      this.novoItemProg = { start_time: "", end_time: "", activity: "", description: "" };
      this.adicionandoItem = false;
      this.salvarProgramacao();
    },

    cancelarAdicionarItem() {
      this.novoItemProg = { start_time: "", end_time: "", activity: "", description: "" };
      this.adicionandoItem = false;
    },

    iniciarEditarProg(idx) {
      this.editandoItemCopia = { ...this.programacao[idx] };
      this.editandoProgIdx = idx;
    },

    confirmarEditarProg() {
      const item = this.programacao[this.editandoProgIdx];
      if (!item.start_time || !item.end_time || !item.activity.trim()) {
        this.$notify({ data: ["Preencha hora início, hora fim e atividade."], type: "warn", duration: 3000 });
        return;
      }
      if (item.end_time <= item.start_time) {
        this.$notify({ data: ["A hora de fim deve ser posterior à hora de início."], type: "warn", duration: 3000 });
        return;
      }
      if (this._temSobreposicao(item.start_time, item.end_time, this.editandoProgIdx)) {
        this.$notify({ data: ["Este horário se sobrepõe a outro item da programação."], type: "warn", duration: 4000 });
        return;
      }
      this.programacao.sort((a, b) => a.start_time.localeCompare(b.start_time));
      this.editandoProgIdx = null;
      this.editandoItemCopia = null;
      this.salvarProgramacao();
    },

    cancelarEditarProg() {
      if (this.editandoProgIdx !== null && this.editandoItemCopia) {
        this.$set(this.programacao, this.editandoProgIdx, { ...this.editandoItemCopia });
      }
      this.editandoProgIdx = null;
      this.editandoItemCopia = null;
    },

    removerItemProg(idx) {
      if (!confirm("Remover este item da programação?")) return;
      this.programacao.splice(idx, 1);
      this.salvarProgramacao();
    },

    salvarProgramacao() {
      if (!this.evento) return;
      this.salvandoProgramacao = true;
      WorkshopService.UpdateSchedule(this.evento.id, this.programacao)
        .then((r) => {
          this.programacao = (r.data.schedule || []).slice().sort((a, b) => a.start_time.localeCompare(b.start_time));
          this.$notify({ data: ["Programação salva com sucesso."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao salvar programação."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.salvandoProgramacao = false; });
    },

    onTabAtivado(newTabIndex) {
      if (newTabIndex === 2) {
        this.skipInscricoes = 0;
        this.carregarInscricoes();
      } else if (newTabIndex === 3) {
        this.carregarQr();
        this.carregarArtworks();
        this.carregarConfirmQr();
        this.carregarConfirmArtworks();
        if (!this.artworkPrompt) this.carregarDefaultPrompt();
        if (!this.confirmPrompt) this.carregarConfirmDefaultPrompt();
      } else if (newTabIndex === 4) {
        this.carregarFotos();
      } else if (newTabIndex === 5 && this.isHojeEvento) {
        this.carregarConfirmados();
      }
    },

    copiarLink(tipo) {
      const url = tipo === "confirmacao" ? this.confirmacaoUrl : this.publicUrl;
      if (!url) return;
      navigator.clipboard.writeText(url).then(() => {
        this.$notify({ data: ["Link copiado!"], type: "success", duration: 2000 });
      });
    },

    // ── Arte / QR ───────────────────────────────────────────────────────────

    carregarQr() {
      if (!this.evento) return;
      this.loadingQr = true;
      const baseUrl = window.location.origin + window.location.pathname.replace(/\/$/, "");
      WorkshopService.GetQrCode(this.evento.id, baseUrl)
        .then((r) => {
          if (this.qrBlobUrl) URL.revokeObjectURL(this.qrBlobUrl);
          this.qrBlobUrl = URL.createObjectURL(r.data);
        })
        .catch(() => {})
        .finally(() => { this.loadingQr = false; });
    },

    baixarQr() {
      if (!this.qrBlobUrl || !this.evento) return;
      const a = document.createElement("a");
      a.href = this.qrBlobUrl;
      a.download = "qrcode-workshop-" + this.evento.id.slice(0, 8) + ".png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    carregarDefaultPrompt() {
      if (!this.evento) return;
      this.loadingDefaultPrompt = true;
      WorkshopService.GetDefaultPrompt(this.evento.id)
        .then((r) => { this.artworkPrompt = r.data.prompt; })
        .catch(() => {})
        .finally(() => { this.loadingDefaultPrompt = false; });
    },

    gerarArte() {
      if (!this.evento || !this.artworkPrompt.trim()) return;
      this.gerandoArte = true;
      const baseUrl = window.location.origin + window.location.pathname.replace(/\/$/, "");
      WorkshopService.GenerateArtwork(this.evento.id, this.artworkPrompt.trim(), this.artworkQuality, baseUrl)
        .then((r) => {
          this.artworks.unshift(r.data);
          this.$notify({ data: ["Arte gerada com sucesso!"], type: "success", duration: 4000 });
        })
        .catch((e) => {
          this.$notify({
            data: [e?.response?.data?.detail || "Erro ao gerar arte. Tente novamente."],
            type: "warn",
            duration: 6000
          });
        })
        .finally(() => { this.gerandoArte = false; });
    },

    carregarArtworks() {
      if (!this.evento) return;
      this.loadingArtworks = true;
      WorkshopService.ListArtworks(this.evento.id, "registration")
        .then((r) => { this.artworks = r.data; })
        .catch(() => {})
        .finally(() => { this.loadingArtworks = false; });
    },

    baixarArte(artwork) {
      if (!artwork.file_url) return;
      const a = document.createElement("a");
      a.href = artwork.file_url;
      a.download = "arte-workshop-" + artwork.id.slice(0, 8) + ".png";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    excluirArte(artwork) {
      if (!this.evento || !confirm("Remover esta arte?")) return;
      WorkshopService.DeleteArtwork(this.evento.id, artwork.id)
        .then(() => {
          this.artworks = this.artworks.filter((a) => a.id !== artwork.id);
          this.$notify({ data: ["Arte removida."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao remover arte."], type: "warn", duration: 5000 });
        });
    },

    uploadArte() {
      if (!this.uploadArquivo || !this.evento) return;
      this.uploadandoArte = true;
      WorkshopService.UploadArtwork(this.evento.id, this.uploadArquivo, "registration")
        .then((r) => {
          this.artworks.unshift(r.data);
          this.uploadArquivo = null;
          this.$notify({ data: ["Arte enviada com sucesso!"], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao enviar arte."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.uploadandoArte = false; });
    },

    // ── Arte de Confirmação ─────────────────────────────────────────────────

    carregarConfirmQr() {
      if (!this.evento) return;
      this.loadingConfirmQr = true;
      const baseUrl = window.location.origin + window.location.pathname.replace(/\/$/, "");
      WorkshopService.GetQrCode(this.evento.id, baseUrl, "confirmation")
        .then((r) => {
          if (this.confirmQrBlobUrl) URL.revokeObjectURL(this.confirmQrBlobUrl);
          this.confirmQrBlobUrl = URL.createObjectURL(r.data);
        })
        .catch(() => {})
        .finally(() => { this.loadingConfirmQr = false; });
    },

    baixarConfirmQr() {
      if (!this.confirmQrBlobUrl || !this.evento) return;
      const a = document.createElement("a");
      a.href = this.confirmQrBlobUrl;
      a.download = "qrcode-confirmacao-" + this.evento.id.slice(0, 8) + ".png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    carregarConfirmDefaultPrompt() {
      if (!this.evento) return;
      this.loadingConfirmPrompt = true;
      WorkshopService.GetDefaultPrompt(this.evento.id, "confirmation")
        .then((r) => { this.confirmPrompt = r.data.prompt; })
        .catch(() => {})
        .finally(() => { this.loadingConfirmPrompt = false; });
    },

    gerarConfirmArte() {
      if (!this.evento || !this.confirmPrompt.trim()) return;
      this.gerandoConfirmArte = true;
      const baseUrl = window.location.origin + window.location.pathname.replace(/\/$/, "");
      WorkshopService.GenerateArtwork(this.evento.id, this.confirmPrompt.trim(), this.confirmQuality, baseUrl, "confirmation")
        .then((r) => {
          this.confirmArtworks.unshift(r.data);
          this.$notify({ data: ["Arte de confirmação gerada com sucesso!"], type: "success", duration: 4000 });
        })
        .catch((e) => {
          this.$notify({
            data: [e?.response?.data?.detail || "Erro ao gerar arte. Tente novamente."],
            type: "warn",
            duration: 6000
          });
        })
        .finally(() => { this.gerandoConfirmArte = false; });
    },

    carregarConfirmArtworks() {
      if (!this.evento) return;
      this.loadingConfirmArtworks = true;
      WorkshopService.ListArtworks(this.evento.id, "confirmation")
        .then((r) => { this.confirmArtworks = r.data; })
        .catch(() => {})
        .finally(() => { this.loadingConfirmArtworks = false; });
    },

    excluirConfirmArte(artwork) {
      if (!this.evento || !confirm("Remover esta arte de confirmação?")) return;
      WorkshopService.DeleteArtwork(this.evento.id, artwork.id)
        .then(() => {
          this.confirmArtworks = this.confirmArtworks.filter((a) => a.id !== artwork.id);
          this.$notify({ data: ["Arte removida."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao remover arte."], type: "warn", duration: 5000 });
        });
    },

    uploadConfirmArte() {
      if (!this.uploadConfirmArquivo || !this.evento) return;
      this.uploadandoConfirmArte = true;
      WorkshopService.UploadArtwork(this.evento.id, this.uploadConfirmArquivo, "confirmation")
        .then((r) => {
          this.confirmArtworks.unshift(r.data);
          this.uploadConfirmArquivo = null;
          this.$notify({ data: ["Arte de confirmação enviada com sucesso!"], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao enviar arte."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.uploadandoConfirmArte = false; });
    },

    // ── Fotos ────────────────────────────────────────────────────────────────

    carregarFotos() {
      if (!this.evento) return;
      this.loadingFotos = true;
      WorkshopService.ListPhotos(this.evento.id)
        .then((r) => {
          this.fotos = r.data.map((f) => ({ ...f, _captionEdit: f.caption }));
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar fotos."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.loadingFotos = false; });
    },

    uploadFoto() {
      if (!this.fotoUploadArquivo || !this.fotoUploadCaption.trim() || !this.evento) return;
      this.uploadandoFoto = true;
      WorkshopService.UploadPhoto(this.evento.id, this.fotoUploadArquivo, this.fotoUploadCaption.trim())
        .then((r) => {
          this.fotos.unshift({ ...r.data, _captionEdit: r.data.caption });
          this.fotoUploadArquivo = null;
          this.fotoUploadCaption = "";
          this.$notify({ data: ["Foto enviada com sucesso!"], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao enviar foto."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.uploadandoFoto = false; });
    },

    salvarCaption(foto) {
      const novaCaption = (foto._captionEdit || "").trim();
      if (!novaCaption || novaCaption === foto.caption) return;
      WorkshopService.UpdatePhoto(this.evento.id, foto.id, { caption: novaCaption })
        .then((r) => {
          const idx = this.fotos.findIndex((f) => f.id === foto.id);
          if (idx !== -1) this.$set(this.fotos, idx, { ...r.data, _captionEdit: r.data.caption });
          this.$notify({ data: ["Legenda atualizada."], type: "success", duration: 2000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao salvar legenda."], type: "warn", duration: 5000 });
        });
    },

    descreverFoto(foto) {
      if (!this.evento) return;
      this.descrevendoId = foto.id;
      WorkshopService.DescribePhoto(this.evento.id, foto.id)
        .then((r) => {
          const idx = this.fotos.findIndex((f) => f.id === foto.id);
          if (idx !== -1) this.$set(this.fotos, idx, { ...r.data, _captionEdit: r.data.caption });
          this.$notify({ data: ["Descrição gerada com sucesso!"], type: "success", duration: 3000 });
        })
        .catch((e) => {
          this.$notify({
            data: [e?.response?.data?.detail || "Erro ao gerar descrição."],
            type: "warn",
            duration: 5000
          });
        })
        .finally(() => { this.descrevendoId = null; });
    },

    excluirFoto(foto) {
      if (!this.evento || !confirm(`Remover a foto "${foto.caption}"?`)) return;
      WorkshopService.DeletePhoto(this.evento.id, foto.id)
        .then(() => {
          this.fotos = this.fotos.filter((f) => f.id !== foto.id);
          this.$notify({ data: ["Foto removida."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao remover foto."], type: "warn", duration: 5000 });
        });
    },

    abrirFoto(foto) {
      if (foto.file_url) window.open(foto.file_url, "_blank");
    },

    // ── Inscrições ──────────────────────────────────────────────────────────

    carregarInscricoes() {
      if (!this.evento) return;
      this.loadingInscricoes = true;
      WorkshopService.ListRegistrations(this.evento.id, this.skipInscricoes, this.limitInscricoes, this.filtroConfirmados)
        .then((r) => {
          this.inscricoes = r.data.items;
          this.totalInscricoes = r.data.total;
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao carregar inscrições."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.loadingInscricoes = false; });
    },

    confirmarInscricao(reg) {
      this.confirmandoId = reg.id;
      WorkshopService.ConfirmRegistration(this.evento.id, reg.id)
        .then((r) => {
          const idx = this.inscricoes.findIndex((x) => x.id === reg.id);
          if (idx !== -1) this.$set(this.inscricoes, idx, r.data);
          this.evento.confirmed = (this.evento.confirmed || 0) + 1;
          this.evento.pending = Math.max(0, (this.evento.pending || 0) - 1);
          this.$notify({ data: ["Presença confirmada."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao confirmar presença."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.confirmandoId = null; });
    },

    removerInscricao(reg) {
      if (!confirm(`Remover a inscrição de "${reg.full_name}"?`)) return;
      this.removendoId = reg.id;
      WorkshopService.DeleteRegistration(this.evento.id, reg.id)
        .then(() => {
          this.inscricoes = this.inscricoes.filter((x) => x.id !== reg.id);
          this.totalInscricoes = Math.max(0, this.totalInscricoes - 1);
          this.evento.total_registrations = Math.max(0, (this.evento.total_registrations || 0) - 1);
          if (reg.confirmed_at) this.evento.confirmed = Math.max(0, (this.evento.confirmed || 0) - 1);
          else this.evento.pending = Math.max(0, (this.evento.pending || 0) - 1);
          this.$notify({ data: ["Inscrição removida."], type: "success", duration: 3000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao remover inscrição."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.removendoId = null; });
    },

    exportarXlsx() {
      if (!this.evento) return;
      this.exportandoXlsx = true;
      WorkshopService.ExportRegistrations(this.evento.id)
        .then((r) => {
          const blob = new Blob([r.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "lista-presenca-workshop.xlsx";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao exportar lista."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.exportandoXlsx = false; });
    },

    proximaPagina() {
      this.skipInscricoes += this.limitInscricoes;
      this.carregarInscricoes();
    },
    paginaAnterior() {
      this.skipInscricoes = Math.max(0, this.skipInscricoes - this.limitInscricoes);
      this.carregarInscricoes();
    },

    // ── Presença no Dia ─────────────────────────────────────────────────────

    fazerCheckin() {
      if (!this.identificadorCheckin.trim() || !this.evento) return;
      this.fazendoCheckin = true;
      this.feedbackCheckin = null;
      WorkshopService.Confirm(this.evento.public_token, this.identificadorCheckin.trim())
        .then((r) => {
          const reg = r.data.registration;
          if (r.data.already_confirmed) {
            this.feedbackCheckin = {
              type: "info",
              message: `${reg.full_name} já havia confirmado presença anteriormente.`
            };
          } else {
            this.feedbackCheckin = {
              type: "success",
              message: `Presença confirmada para ${reg.full_name}!`
            };
            this.confirmadosPresenca.unshift(reg);
            this.evento.confirmed = (this.evento.confirmed || 0) + 1;
            this.evento.pending = Math.max(0, (this.evento.pending || 0) - 1);
          }
          this.identificadorCheckin = "";
          this.carregarConfirmados();
        })
        .catch((e) => {
          this.feedbackCheckin = {
            type: "danger",
            message: e?.response?.data?.detail || "Não encontrado. Verifique os dados informados."
          };
        })
        .finally(() => { this.fazendoCheckin = false; });
    },

    carregarConfirmados() {
      if (!this.evento) return;
      this.loadingConfirmados = true;
      WorkshopService.ListRegistrations(this.evento.id, 0, 100, true)
        .then((r) => {
          this.confirmadosPresenca = r.data.items.sort((a, b) =>
            (b.confirmed_at || "").localeCompare(a.confirmed_at || "")
          );
        })
        .catch(() => {})
        .finally(() => { this.loadingConfirmados = false; });
    },

    // ── Modal criar ─────────────────────────────────────────────────────────

    abrirModalCriar() {
      this.novoEvento = NOVO_EVENTO_VAZIO();
      this.modalCriar = true;
    },
    fecharModalCriar() {
      this.novoEvento = NOVO_EVENTO_VAZIO();
    },
    criarEvento(evt) {
      evt.preventDefault();
      if (!this.novoEvento.title.trim() || !this.novoEvento.event_date || !this.novoEvento.location.trim()) {
        this.$notify({ data: ["Preencha título, data e local."], type: "warn", duration: 3000 });
        return;
      }
      this.criando = true;
      const payload = {
        title: this.novoEvento.title.trim(),
        description: this.novoEvento.description || null,
        event_date: this.localToIso(this.novoEvento.event_date),
        location: this.novoEvento.location.trim(),
        registration_deadline: this.novoEvento.registration_deadline
          ? this.localToIso(this.novoEvento.registration_deadline)
          : null,
        max_capacity: this.novoEvento.max_capacity || null,
        status: "draft"
      };
      WorkshopService.CreateEvent(this.contaId, payload)
        .then((r) => {
          this.evento = {
            ...r.data,
            event_date_local: this.isoToLocal(r.data.event_date),
            registration_deadline_local: r.data.registration_deadline ? this.isoToLocal(r.data.registration_deadline) : ""
          };
          this.modalCriar = false;
          this.$notify({ data: ["Workshop criado com sucesso!"], type: "success", duration: 4000 });
        })
        .catch(() => {
          this.$notify({ data: ["Erro ao criar workshop."], type: "warn", duration: 5000 });
        })
        .finally(() => { this.criando = false; });
    },

    // ── Utilitários ─────────────────────────────────────────────────────────

    statusLabel(s) {
      return { draft: "Rascunho", open: "Aberto", closed: "Encerrado", completed: "Realizado" }[s] || s;
    },
    statusVariant(s) {
      return { draft: "secondary", open: "success", closed: "warning", completed: "primary" }[s] || "secondary";
    },
    metodoLabel(m) {
      return { email: "e-mail", phone: "telefone", name: "nome", manual: "admin" }[m] || m || "—";
    },
    formatarData(iso) {
      return iso ? new Date(iso).toLocaleString("pt-BR") : "—";
    },
    formatarDataSimples(iso) {
      return iso ? new Date(iso).toLocaleDateString("pt-BR", {
        weekday: "long", day: "2-digit", month: "long", year: "numeric"
      }) : "";
    },
    formatarHora(iso) {
      return iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
    },
    isoToLocal(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return (
        d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
        "T" + pad(d.getHours()) + ":" + pad(d.getMinutes())
      );
    },
    localToIso(local) {
      if (!local) return null;
      return new Date(local).toISOString();
    }
  }
};
</script>
